/**
 * @file Modal Registry
 *
 * Central registry for all modals in the HotPads application.
 * This is the main entry point for modal configuration and management.
 *
 * Types are defined in ./types.ts for better organization.
 * This index file provides the modal registry and utility functions.
 */

import type React from 'react';
import { lazy } from 'react';
import type { BaseModalProps, ModalType } from './types';

// Re-export types for convenience
export type { BaseModalProps, ModalType, ModalPropsMap } from './types';

/**
 * Basic modal configuration - can be expanded as needed
 */
export interface ModalConfiguration {
  component: React.ComponentType<any>;
  defaultProps?: Partial<BaseModalProps>;
}

/**
 * Registry of available modals - to be populated as modals are migrated
 */
export const MODAL_CONFIGS: Partial<Record<ModalType, ModalConfiguration>> = {
  costsAndFees: {
    component: lazy(
      () =>
        import(
          /* webpackChunkName: "costs-and-fees-modal" */
          /* webpackPrefetch: true */
          'app/shared/modules/hdp/CostsAndFees/CostsAndFeesModalContent'
        ),
    ),
    defaultProps: {
      isWebview: false,
      size: 'xxl', // Max width 1240px (closest to 1200px)
      scrollablePaddingInline: '2x', // 16px padding on sides
      paddingTop: '2x',
      paddingBottom: '1x',
    },
  },

  contact: {
    component: lazy(
      () =>
        import(
          /* webpackChunkName: "contact-modal" */
          /* webpackPrefetch: true */
          'app/shared/modules/hdp/form/Contact/Contact'
        ),
    ),
    defaultProps: {
      hideHeader: true,
      isPopup: true,
      clientLoaded: true,
    },
  },

  listingCardContact: {
    component: lazy(
      () =>
        import(
          /* webpackChunkName: "srp-contact-modal" */
          /* webpackPrefetch: true */
          'app/shared/modules/listing-cards/ListingCardV3/ListingCardContactModal'
        ),
    ),
    defaultProps: {
      hideHeader: true,
      isPopup: true,
      clientLoaded: true,
    },
  },

  postContactRenterProfile: {
    component: lazy(
      () =>
        import(
          /* webpackChunkName: "post-contact-renter-profile-modal" */
          /* webpackPrefetch: true */
          'app/shared/modules/popups/PostContactRenterProfile'
        ),
    ),
    defaultProps: {
      hideHeader: true,
      paddingTop: '2x',
      paddingBottom: '2x',
      transparentOverlay: true,
    },
  },

  postContactSavedSearch: {
    component: lazy(
      () =>
        import(
          /* webpackChunkName: "post-contact-saved-search-modal" */
          /* webpackPrefetch: true */
          'app/shared/modules/PostContactSavedSearch/ModalWrapper'
        ),
    ),
    defaultProps: {
      hideHeader: true,
      minWidth: '332px',
    },
  },

  promptLogin: {
    component: lazy(
      () =>
        import(
          /* webpackChunkName: "prompt-login-modal" */
          /* webpackPrefetch: true */
          'app/shared/modules/auth/PromptLogin/ModalWrapper'
        ),
    ),
    defaultProps: {
      hideHeader: false,
      size: '320',
      paddingTop: '6x',
      paddingBottom: '4x',
      paddingInline: '2x',
    },
  },

  contactSuccess: {
    component: lazy(
      () =>
        import(
          /* webpackChunkName: "contact-success-modal" */
          /* webpackPrefetch: true */
          'app/shared/modules/hdp/form/Contact/ContactSuccess'
        ),
    ),
    defaultProps: {
      hideHeader: true,
      paddingTop: '0',
      paddingBottom: '0',
    },
  },

  waitlistSuccess: {
    component: lazy(
      () =>
        import(
          /* webpackChunkName: "waitlist-success-modal" */
          /* webpackPrefetch: true */
          'app/shared/modules/hdp/form/WaitlistSuccessPopup'
        ),
    ),
    defaultProps: {
      hideHeader: true,
      paddingTop: '2x',
      paddingBottom: '2x',
    },
  },
};

/**
 * Get modal component for a given type
 * Returns null if modal is not registered, with optional console warning in development
 */
export const getModalComponent = (modalType: ModalType): React.ComponentType<any> | null => {
  const config = MODAL_CONFIGS[modalType];

  if (!config && process.env.NODE_ENV !== 'production') {
    console.warn(
      `Modal type "${modalType}" is not registered. Register it in MODAL_CONFIGS or check if it's been migrated to the ModalContext system.`,
    );
  }

  return config?.component || null;
};

/**
 * Check if a modal type is registered
 */
export const isModalRegistered = (modalType: ModalType): boolean => {
  return modalType in MODAL_CONFIGS;
};

/**
 * Get default props for a specific modal type.
 * These props are merged with user-provided props when opening the modal.
 * @param type - The modal type key from MODAL_CONFIGS
 * @returns Object containing default props (size, padding, etc.)
 */
export const getModalDefaultProps = (type: ModalType): Record<string, any> => {
  const config = MODAL_CONFIGS[type];
  return config?.defaultProps || {};
};

/**
 * Register a new modal in the system
 */
export const registerModal = (modalType: ModalType, config: ModalConfiguration): void => {
  MODAL_CONFIGS[modalType] = config;
};

/**
 * Preload a modal's chunk to avoid lazy-loading pop-in when opened.
 * Useful for modals triggered by URL parameters or conditionally loaded based on route.
 *
 * This function triggers the dynamic import without waiting for it to complete,
 * allowing the browser to fetch the chunk in the background.
 *
 * @param modalType - The modal type to preload
 * @returns Promise that resolves when the chunk is loaded, or undefined if modal not found
 *
 * @example
 * // Preload contact modal on pad/building pages
 * if (routeUtils.isPadOrBuildingUrl(pathname)) {
 *   preloadModalChunk('contact');
 * }
 */
export const preloadModalChunk = (modalType: ModalType): Promise<any> | undefined => {
  // Map of modal types to their dynamic import functions
  // This mirrors the lazy() imports in MODAL_CONFIGS but returns the promise directly
  const preloadMap: Partial<Record<ModalType, () => Promise<any>>> = {
    contact: () =>
      import(
        /* webpackChunkName: "contact-modal" */
        /* webpackPrefetch: true */
        'app/shared/modules/hdp/form/Contact/Contact'
      ),
    listingCardContact: () =>
      import(
        /* webpackChunkName: "srp-contact-modal" */
        /* webpackPrefetch: true */
        'app/shared/modules/listing-cards/ListingCardV3/ListingCardContactModal'
      ),
    costsAndFees: () =>
      import(
        /* webpackChunkName: "costs-and-fees-modal" */
        /* webpackPrefetch: true */
        'app/shared/modules/hdp/CostsAndFees/CostsAndFeesModalContent'
      ),
    postContactRenterProfile: () =>
      import(
        /* webpackChunkName: "post-contact-renter-profile-modal" */
        /* webpackPrefetch: true */
        'app/shared/modules/popups/PostContactRenterProfile'
      ),
    postContactSavedSearch: () =>
      import(
        /* webpackChunkName: "post-contact-saved-search-modal" */
        /* webpackPrefetch: true */
        'app/shared/modules/PostContactSavedSearch/ModalWrapper'
      ),
    promptLogin: () =>
      import(
        /* webpackChunkName: "prompt-login-modal" */
        /* webpackPrefetch: true */
        'app/shared/modules/auth/PromptLogin/ModalWrapper'
      ),
    contactSuccess: () =>
      import(
        /* webpackChunkName: "contact-success-modal" */
        /* webpackPrefetch: true */
        'app/shared/modules/hdp/form/Contact/ContactSuccess'
      ),
    waitlistSuccess: () =>
      import(
        /* webpackChunkName: "waitlist-success-modal" */
        /* webpackPrefetch: true */
        'app/shared/modules/hdp/form/WaitlistSuccessPopup'
      ),
  };

  const preloadFn = preloadMap[modalType];
  if (preloadFn) {
    return preloadFn();
  }

  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(`Cannot preload modal type "${modalType}" - no preload mapping found.`);
  }

  return undefined;
};
